import React, { useState, useRef, useEffect } from "react";
import { useDispatch } from "react-redux";
import { useAppSelector } from "redux/hooks";
import { useForm, Controller } from "react-hook-form";
import { InputNumber } from "primereact/inputnumber";
import { InputSwitch } from "primereact/inputswitch";
import { Button } from "primereact/button";
import { ScrollPanel } from "primereact/scrollpanel";
import InformationBox from "components/common/Message/InformationBox/InformationBox";
import { getPasswordPolicyData, onUpdatePasswordPolicyData } from "./passwordPolicySlice";
import { BsExclamationCircleFill } from "react-icons/bs";
import { classNames } from "primereact/utils";

interface Props {}

export const AccountActivityPolicy: React.FC<Props> = () => {
  type FormData = {
    atMostDaysOfInactive: number;
    atMostDaysOfDelete: number;
  };

  const passwordPolicyData = useAppSelector((state) => state.passwordPolicy.passwordPolicyData);
  const [displayInfoModal, setDisplayInfoModal] = useState(false);
  const [message, setMessage] = useState("");
  const [autoDisableInactiveAccount, setAutoDisableInactiveAccount] = useState(false);
  const [autoDeleteInactiveAccount, setAutoDeleteInactiveAccount] = useState(false);
  const [allowUsersToReactivate, setAllowUsersToReactivate] = useState(false);

  const formRef: any = useRef();
  const { control, handleSubmit, setValue, formState: { errors } } = useForm<FormData>({
    defaultValues: {
      atMostDaysOfInactive: undefined,
      atMostDaysOfDelete: undefined,
    },
  });

  const dispatch = useDispatch<any>();

  useEffect(() => {
    dispatch(getPasswordPolicyData());
  }, []);

  useEffect(() => {
    if (passwordPolicyData) {
      setAutoDisableInactiveAccount(passwordPolicyData?.isAutoDisableOnInactive);
      setValue("atMostDaysOfInactive", passwordPolicyData?.atMostDaysOfInactive);
      setAutoDeleteInactiveAccount(!passwordPolicyData?.isNeverDelete);
      setValue("atMostDaysOfDelete", passwordPolicyData?.atMostDaysOfDelete);
      setAllowUsersToReactivate(passwordPolicyData?.allowUsersToReactivateInactiveAccount);
    }
  }, [passwordPolicyData]);

  const onFormSubmit = (data: any) => {
    const body = {
      ...passwordPolicyData,
      isAutoDisableOnInactive: autoDisableInactiveAccount,
      atMostDaysOfInactive: data.atMostDaysOfInactive,
      isNeverDelete: !autoDeleteInactiveAccount,
      atMostDaysOfDelete: data.atMostDaysOfDelete,
      allowUsersToReactivateInactiveAccount: allowUsersToReactivate,
    };
    dispatch(onUpdatePasswordPolicyData(body, setMessage, setDisplayInfoModal));
  };

  const maxLengthValidation = (value: number, name: string) => {
    if (name === "atMostDaysOfInactive" && (value < 1 || value > 365))
      return "Lock inactive account should be between 1 and 365";
    if (name === "atMostDaysOfDelete" && (value < 1 || value > 365))
      return "Delete inactive account should be between 1 and 365";
  };

  const getFormErrorMessage = (name: string) =>
    errors[name] && (
      <span className="tooltip-text">
        <BsExclamationCircleFill />
        {errors[name].message}
      </span>
    );

  return (
    <ScrollPanel style={{ width: "100%", height: "100%" }}>
      <div className="main-passwordpolicy">
        <p className="Pagetitle mb-0">
          <b>Account Activity Policy</b>
        </p>
        <form onSubmit={handleSubmit(onFormSubmit)} ref={formRef}>
          <div className="row">
            <div className="col-6">
              <p className="subtitle-passwordpolicy mb-0">Inactive Account Settings</p>
              <hr />
              <div className="password-requirements">
                <div className="pt-2 pl-2 d-flex align-items-center">
                  <InputSwitch
                    checked={autoDisableInactiveAccount}
                    onChange={(e) => setAutoDisableInactiveAccount(e.value)}
                  />
                  <span className="ml-2">Auto-disable inactive accounts</span>
                </div>
                {autoDisableInactiveAccount && (
                  <div className="pt-2 pl-2 d-flex align-items-center">
                    <span>Lock after</span>
                    <span className="ml-2 mr-2 position-relative">
                      <Controller
                        name="atMostDaysOfInactive"
                        control={control}
                        rules={{
                          required: "This field is required.",
                          validate: (v) => maxLengthValidation(v, "atMostDaysOfInactive"),
                        }}
                        render={({ field }) => (
                          <InputNumber
                            id="atMostDaysOfInactive"
                            value={field.value}
                            onValueChange={(e) => field.onChange(e.value)}
                            className={classNames({ "p-invalid": errors.atMostDaysOfInactive })}
                          />
                        )}
                      />
                      {getFormErrorMessage("atMostDaysOfInactive")}
                    </span>
                    <span>days of inactivity</span>
                  </div>
                )}

                <div className="pt-2 pl-2 d-flex align-items-center">
                  <InputSwitch
                    checked={autoDeleteInactiveAccount}
                    onChange={(e) => setAutoDeleteInactiveAccount(e.value)}
                  />
                  <span className="ml-2">Auto-delete inactive accounts</span>
                </div>
                {autoDeleteInactiveAccount && (
                  <div className="pt-2 pl-2 d-flex align-items-center">
                    <span>Delete after</span>
                    <span className="ml-2 mr-2 position-relative">
                      <Controller
                        name="atMostDaysOfDelete"
                        control={control}
                        rules={{
                          required: "This field is required.",
                          validate: (v) => maxLengthValidation(v, "atMostDaysOfDelete"),
                        }}
                        render={({ field }) => (
                          <InputNumber
                            id="atMostDaysOfDelete"
                            value={field.value}
                            onValueChange={(e) => field.onChange(e.value)}
                            className={classNames({ "p-invalid": errors.atMostDaysOfDelete })}
                          />
                        )}
                      />
                      {getFormErrorMessage("atMostDaysOfDelete")}
                    </span>
                    <span>days of inactivity</span>
                  </div>
                )}

                <div className="pt-2 pl-2 d-flex align-items-center">
                  <InputSwitch
                    checked={allowUsersToReactivate}
                    onChange={(e) => setAllowUsersToReactivate(e.value)}
                  />
                  <span className="ml-2">Allow users to reactivate their inactive account</span>
                </div>
              </div>

            </div>
          </div>
        </form>
        <div className="footer-btn">
          <Button
            label="Save Changes"
            className="save-btn"
            onClick={() => formRef.current.requestSubmit()}
          />
        </div>
      </div>
      <InformationBox
        showInfoModal={displayInfoModal}
        setShowInfoModal={setDisplayInfoModal}
        message={message}
      />
    </ScrollPanel>
  );
};

export default AccountActivityPolicy;
